/**
 * Date utility functions for formatting dates with local timezone support
 */

/**
 * Parse a UTC date string and return a Date object
 * Backend sends datetime without 'Z' suffix, so we need to explicitly treat it as UTC
 * @param {string} dateString - ISO 8601 date string (UTC, without timezone)
 * @returns {Date} Date object in UTC
 */
export const parseUTCDate = (dateString) => {
  if (!dateString) return null;

  // If the string already has 'Z' or timezone offset, parse directly
  if (dateString.endsWith('Z') || dateString.includes('+') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
    return new Date(dateString);
  }

  // Otherwise, append 'Z' to indicate UTC
  return new Date(dateString + 'Z');
};

/**
 * Format a date string to local timezone with full date and time
 * @param {string} dateString - ISO 8601 date string (UTC)
 * @returns {string} Formatted date string in local timezone
 */
export const formatFullDate = (dateString) => {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format a date string to relative time (e.g., "2h ago") or short date
 * @param {string} dateString - ISO 8601 date string (UTC)
 * @returns {string} Formatted relative time or short date
 */
export const formatRelativeDate = (dateString) => {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

/**
 * Format a date string to short date format
 * @param {string} dateString - ISO 8601 date string (UTC)
 * @returns {string} Formatted short date string
 */
export const formatDateCompact = (dateString) => {
  if (!dateString) return '';
  const date = parseUTCDate(dateString);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatShortDate = (dateString) => {
  if (!dateString) return '';

  const date = parseUTCDate(dateString);
  if (!date) return '';

  const now = new Date();

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};
